import { Injectable } from '@angular/core';
import { Constants } from '../helpers/constants';
import { CPJP_INSTANCE_CONFIG, FFM_INSTANCE_CONFIG, PRSPR_INSTANCE_CONFIG } from './instance-configs';
import { InstanceConfig, InstanceKey, InstanceLabels } from './instance-config.model';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class InstanceService {
  private normalizeKey(raw: string | null | undefined): InstanceKey | null {
    const key = (raw || '').toLowerCase().trim();
    if (key === 'ffm' || key === 'prspr' || key === 'cpjp') return key;
    return null;
  }

  private originFromEnv(url: string | undefined): string | null {
    const trimmed = (url || '').trim();
    if (!trimmed) return null;
    try {
      return new URL(trimmed, window.location.href).origin;
    } catch {
      return null;
    }
  }

  /**
   * Best-effort inference when localStorage is empty/stale.
   * This fixes cases where the instance handoff cookies fail and the app would otherwise default to FFM.
   */
  private inferInstanceKey(): InstanceKey | null {
    // Local dev convention: ports map to instances
    switch (window.location.port) {
      case '4202':
        return 'prspr';
      case '4204':
        return 'cpjp';
      case '4200':
        return 'ffm';
    }

    const currentOrigin = window.location.origin;

    const prsprOrigin = this.originFromEnv(environment.PrsprFrontendUrl);
    if (prsprOrigin && prsprOrigin === currentOrigin) return 'prspr';

    const cpjpOrigin = this.originFromEnv(environment.cpjpFrontendUrl);
    if (cpjpOrigin && cpjpOrigin === currentOrigin) return 'cpjp';

    const ffmOrigin = this.originFromEnv(environment.ffmFrontendUrl);
    if (ffmOrigin && ffmOrigin === currentOrigin) return 'ffm';

    return null;
  }

  getKey(): InstanceKey {
    const stored = this.normalizeKey(localStorage.getItem(Constants.LS_CURRENT_INSTANCE));
    const inferred = this.inferInstanceKey();

    // If we can strongly infer instance from origin/port, prefer it over a stale localStorage value.
    if (inferred && stored && stored !== inferred) {
      localStorage.setItem(Constants.LS_CURRENT_INSTANCE, inferred);
      return inferred;
    }

    if (stored) return stored;

    const fallback = inferred ?? 'ffm';
    localStorage.setItem(Constants.LS_CURRENT_INSTANCE, fallback);
    return fallback;
  }

  getConfig(): InstanceConfig {
    switch (this.getKey()) {
      case 'cpjp':
        return CPJP_INSTANCE_CONFIG;
      case 'prspr':
        return PRSPR_INSTANCE_CONFIG;
      case 'ffm':
      default:
        return FFM_INSTANCE_CONFIG;
    }
  }

  get labels(): InstanceLabels {
    return this.getConfig().labels;
  }

  get isAltLayout(): boolean {
    return !!this.getConfig()?.features?.useAltLayout;
  }

  get isLastNameFirst(): boolean {
    const rawLang = (localStorage.getItem(Constants.LS_LANGUAGE) || '').trim();
    const normalized = rawLang.toLowerCase().replace('_', '-');
    const primary = normalized.split('-')[0];
    return primary === 'ja' || primary === 'ko';
  }

  is(key: InstanceKey): boolean {
    return this.getKey() === key;
  }
}
